import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Film, Image, Map, ArrowRight } from 'lucide-react';
import ManifestationTemplateLibrary from '@/components/ManifestationTemplateLibrary';
import VisionBoardCreator from '@/components/VisionBoardCreator';
import { supabase } from '@/integrations/supabase/client';
import { PageHeader } from '@/components/PageHeader';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { User } from '@supabase/supabase-js';
import { useNavigationHistory } from '@/hooks/useNavigationHistory';
import { useEditableContent } from '@/hooks/useEditableContent';
import { ContextualNotificationPrompt } from '@/components/notifications/ContextualNotificationPrompt';

export default function Manifestation() {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  
  useNavigationHistory(); // Track navigation
  
  // Editable content
  const ctaTitle = useEditableContent('manifestation.cta.title', 'Ready to Make It Real?', 'Manifestation');
  const ctaDescription = useEditableContent('manifestation.cta.description', 'You\'ve created your vision-now let\'s build the practical navigation system to get you there. Map out your timeline, set pivotal steps, and embody your future self daily.', 'Manifestation');
  const ctaButton = useEditableContent('manifestation.cta.button', 'Create Your Navigation System', 'Manifestation');
  const tabMovieLabel = useEditableContent('manifestation.tab.movie', 'Manifestation Movie', 'Manifestation');
  const tabVisionLabel = useEditableContent('manifestation.tab.vision', 'Vision Board', 'Manifestation');

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
    });
    
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <div className="min-h-screen bg-background">
      <PageHeader title="Manifestation" />

      <div className="container mx-auto px-4 py-8">
        {/* Contextual notification prompt */}
        <ContextualNotificationPrompt context="manifestation" className="mb-6" />

        <div className="text-center mb-6">
          <div className="inline-block px-3 py-1 mb-3 text-xs font-medium bg-primary/20 text-primary rounded-full">
            FUTURE TIMELINE
          </div>
          <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
            Create your vision and consciously design your <span className="text-primary font-medium">Future Timeline</span>.
          </p>
        </div>
        {/* Call to Action for Future Self Navigator */}
        <Card className="mb-8 bg-gradient-to-br from-primary/10 via-background to-background border-primary/20 animate-fade-in">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-full bg-primary/10">
                    <Map className="h-6 w-6 text-primary" />
                  </div>
                  <h2 className="text-xl font-bold text-foreground">{ctaTitle}</h2>
                </div>
                <p className="text-muted-foreground">
                  {ctaDescription}
                </p>
              </div>
              <Button 
                size="lg"
                onClick={() => navigate('/future-self-navigator')}
                className="flex items-center gap-2 whitespace-nowrap"
              >
                {ctaButton}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="movie" className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2">
            <TabsTrigger value="movie" className="flex items-center gap-2">
              <Film className="h-4 w-4" />
              {tabMovieLabel}
            </TabsTrigger>
            <TabsTrigger value="vision" className="flex items-center gap-2">
              <Image className="h-4 w-4" />
              {tabVisionLabel}
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="movie" className="mt-6">
            <ManifestationTemplateLibrary />
          </TabsContent>
          
          <TabsContent value="vision" className="mt-6">
            <VisionBoardCreator user={user} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}